import { useMemo, useState } from 'react';
import { CUSTOM_CATEGORY_OPTIONS } from '../../makeup-flux-lab/utils/makeupCustomOptions';
import {
  FLUX_LAB_FINISH_CATEGORIES,
  FLUX_LAB_FINISH_OPTIONS,
  finishSelectValue,
} from '../../makeup-flux-lab/utils/makeupFluxLabFinishes';
import { optionIdForLayer } from '../../makeup-flux-lab/utils/makeupFluxLabControls';
import { isLayerActive } from '../../makeup-flux-lab/utils/makeupLabResolve';

const CATEGORY_LABELS = {
  foundation: 'Foundation',
  concealer: 'Concealer',
  blush: 'Blush',
  eyeshadow: 'Eyeshadow',
  eyeliner: 'Eyeliner',
  eyelash: 'Eyelash',
  lipstick: 'Lipstick',
  highlighter: 'Highlighter',
  contour: 'Contour',
  eyebrows: 'Brows',
};

function isHexOption(opt) {
  const id = String(opt?.id || '');
  return /^#[0-9A-Fa-f]{6}$/.test(id) || Boolean(opt?.layer?.color);
}

function swatchHex(opt) {
  const id = String(opt?.id || '');
  if (/^#[0-9A-Fa-f]{6}$/.test(id)) return id;
  return opt?.layer?.color || null;
}

export default function MakeupV3ColorEditor({
  categories,
  makeupLab,
  onCategory,
  onIntensity,
  onFinish,
  busy,
}) {
  const [activeCategory, setActiveCategory] = useState(() => categories[0] || 'blush');
  const [editorTab, setEditorTab] = useState('colors');

  const options = useMemo(
    () => CUSTOM_CATEGORY_OPTIONS[activeCategory] || [],
    [activeCategory],
  );

  const selectedId = optionIdForLayer(activeCategory, makeupLab[activeCategory]) || 'none';
  const layer = makeupLab[activeCategory];
  const categoryOn = isLayerActive(layer);
  const colorOptions = options.filter((o) => o.id !== 'none' && isHexOption(o));
  const specialOptions = options.filter((o) => o.id !== 'none' && !isHexOption(o));
  const showFinish =
    FLUX_LAB_FINISH_CATEGORIES.includes(activeCategory) &&
    categoryOn &&
    (FLUX_LAB_FINISH_OPTIONS[activeCategory] || []).length > 0;

  return (
    <div className="looks-custom makeup-v3-color-editor">
      <div className="looks-custom__cats" role="tablist" aria-label="Makeup categories">
        {categories.map((cat) => (
          <button
            key={cat}
            type="button"
            role="tab"
            aria-selected={activeCategory === cat}
            className={`looks-custom__cat${activeCategory === cat ? ' is-active' : ''}${
              isLayerActive(makeupLab[cat]) ? ' is-on' : ''
            }`}
            onClick={() => {
              setActiveCategory(cat);
              setEditorTab('colors');
            }}
          >
            {CATEGORY_LABELS[cat] || cat}
          </button>
        ))}
      </div>

      <div className="looks-custom__editor">
        <div className="looks-custom__subtabs" role="tablist" aria-label={`${CATEGORY_LABELS[activeCategory]} options`}>
          <button
            type="button"
            role="tab"
            className={`looks-custom__subtab${editorTab === 'colors' ? ' is-active' : ''}`}
            aria-selected={editorTab === 'colors'}
            onClick={() => setEditorTab('colors')}
          >
            Colors
          </button>
          {showFinish ? (
            <button
              type="button"
              role="tab"
              className={`looks-custom__subtab${editorTab === 'finish' ? ' is-active' : ''}`}
              aria-selected={editorTab === 'finish'}
              onClick={() => setEditorTab('finish')}
            >
              Finish
            </button>
          ) : null}
          <button
            type="button"
            role="tab"
            className={`looks-custom__subtab${editorTab === 'adjust' ? ' is-active' : ''}`}
            aria-selected={editorTab === 'adjust'}
            onClick={() => setEditorTab('adjust')}
          >
            Strength
          </button>
        </div>

        {editorTab === 'colors' ? (
          <div className="looks-swatch-grid">
            <button
              type="button"
              title="None"
              className={`looks-swatch looks-swatch--none${!categoryOn ? ' is-selected' : ''}`}
              disabled={busy}
              onClick={() => onCategory(activeCategory, 'none')}
            >
              <span>None</span>
            </button>
            {specialOptions.map((opt) => (
              <button
                key={opt.id}
                type="button"
                title={opt.label}
                className={`looks-swatch looks-swatch--label${selectedId === opt.id ? ' is-selected' : ''}`}
                disabled={busy}
                onClick={() => onCategory(activeCategory, opt.id)}
              >
                <span>{opt.label}</span>
              </button>
            ))}
            {colorOptions.map((opt) => {
              const hex = swatchHex(opt);
              return (
                <button
                  key={opt.id}
                  type="button"
                  title={opt.label}
                  className={`looks-swatch${selectedId === opt.id ? ' is-selected' : ''}`}
                  style={hex ? { backgroundColor: hex } : undefined}
                  disabled={busy}
                  onClick={() => onCategory(activeCategory, opt.id)}
                />
              );
            })}
          </div>
        ) : null}

        {editorTab === 'finish' && showFinish ? (
          <div className="looks-finish-row" role="group" aria-label="Finish">
            {(FLUX_LAB_FINISH_OPTIONS[activeCategory] || []).map((f) => (
              <button
                key={f.id}
                type="button"
                className={`looks-finish${
                  finishSelectValue(layer, activeCategory) === f.id ? ' is-active' : ''
                }`}
                disabled={busy}
                onClick={() => onFinish(activeCategory, f.id)}
              >
                {f.name}
              </button>
            ))}
          </div>
        ) : null}

        {editorTab === 'adjust' ? (
          <label className="mfvp3-row mfvp3-row--intensity makeup-v3-color-editor__strength">
            <span>Strength {categoryOn ? layer?.intensity ?? 0 : 0}%</span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              disabled={busy || !categoryOn}
              value={categoryOn ? layer?.intensity ?? 0 : 0}
              onChange={(e) => onIntensity(activeCategory, e.target.value)}
            />
          </label>
        ) : null}
      </div>
    </div>
  );
}
